import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Container, Table, Button, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import WeekSelector from "../weekSelector";
import { useTranslation } from 'react-i18next';

const visibleColumns = {
  uber: ["driver_firstName", "driver_lastName", "incBrutto", "incNetto", "incCash", "appFee", "appFeeTaxes", "bonus", "tips"],
  bolt: ["driver_firstName", "driver_lastName", "incBrutto", "incBruttoApp", "incBruttoCash", "incNettoCash", "appFee", "incNetto", "totalInc", "bonus", "tips"]
};

const EditableIncoming = () => {
  const { service } = useParams();
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingCell, setEditingCell] = useState({ row: null, key: null });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [invalidUsers, setInvalidUsers] = useState([]);
  const { t } = useTranslation();

  const columnLabels = {
    driver_firstName: t("name"),
    driver_lastName: t("surname"),
    incBrutto: t("brutto"),
    incNetto: t("netto"),
    incCash: t("cash"),
    appFee: t("appfee"),
    appFeeTaxes: `${t("appfee")} ${t("tax")}`,
    bonus: t("bonus"),
    tips: t("tips"),
    incBruttoApp: t("brutto"),
    incBruttoCash: `${t("brutto")} ${t("cash")}`,
    incNettoCash: `${t("netto")} ${t("cash")}`,
    totalInc: t("total")
  };

  const fetchData = async () => {
    if (!selectedWeek) return;
    setLoading(true);
    try {
      const res = await axios.get(`https://godrive.com.pl/api/get-${service}-data`, {
        params: { week_number: selectedWeek },
      });
      const data = res.data;

      const invalidChecks = await Promise.all(
        data.map(async (row) => {
          const { driver_firstName, driver_lastName } = row;
          try {
            const res = await axios.get("https://godrive.com.pl/api/user-exists", {
              params: { firstName: driver_firstName, lastName: driver_lastName },
            });
            return res.data.exists ? null : `${driver_firstName} ${driver_lastName}`;
          } catch (e) {
            return null;
          }
        })
      );

      const invalidList = invalidChecks.filter(Boolean);
      setInvalidUsers(invalidList);
      setTableData(data);
      setMessage(null);
    } catch (error) {
      console.error("❌ Помилка завантаження:", error);
      setMessage("Не вдалося завантажити дані з сервера.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (rowIndex, key, value) => {
    const updatedData = [...tableData];
    updatedData[rowIndex][key] = value;
    setTableData(updatedData);
  };

  const token = localStorage.getItem("token");

  const deleteRecord = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await axios.delete(
        `https://godrive.com.pl/api/${service}/record/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setTableData((prev) => prev.filter((row) => row.id !== id));
    } catch (error) {
      console.error("❌ Помилка при видаленні:", error);
      alert(t("deleteError"));
    }
  };

  const saveChanges = async () => {
    try {
      for (const row of tableData) {
        if (!row.id) continue;

        const cleanRecord = Object.fromEntries(
          Object.entries(row).filter(
            ([k, v]) => v !== null && v !== undefined && v !== ""
          )
        );

        if (cleanRecord.created_at) {
          try {
            const date = new Date(cleanRecord.created_at);
            cleanRecord.created_at = date.toISOString().slice(0, 19).replace('T', ' ');
          } catch (e) {
            console.warn("⚠️ Неможливо конвертувати дату:", cleanRecord.created_at);
          }
        }

        await axios.put(`https://godrive.com.pl/api/${service}/record/${row.id}`, cleanRecord);
      }
      alert("✅ Зміни збережено");
    } catch (error) {
      console.error("❌ Помилка збереження:", error);
      alert("❌ Помилка при збереженні змін");
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getComparableValue = (value) => {
    const num = parseFloat(value);
    return !isNaN(num) && isFinite(num) ? num : String(value).toLowerCase();
  };

  const sortedData = [...tableData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = getComparableValue(a[sortConfig.key]);
    const bVal = getComparableValue(b[sortConfig.key]);

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
    }
    return sortConfig.direction === "asc"
      ? aVal.localeCompare(bVal)
      : bVal.localeCompare(aVal);
  });

  useEffect(() => {
    fetchData();
  }, [selectedWeek]);

  const currentColumns = visibleColumns[service] || [];

  return (
    <Container className="mt-4">
      <h2 className="text-center mb-4">{t("edit")} {t("records")}: {service}</h2>

      <div className="mb-3">
        <label>{t("choose")} {t("week").toLocaleLowerCase()}:</label>
        <WeekSelector selectedWeek={selectedWeek} setSelectedWeek={setSelectedWeek} />
      </div>

      {loading ? (
        <Alert variant="info">{t("loading")}...</Alert>
      ) : message ? (
        <Alert variant="danger">{message}</Alert>
      ) : sortedData.length === 0 ? (
        <Alert variant="warning">{t("nooRecords")}</Alert>
      ) : (
        <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
          <Table bordered striped hover>
            <thead className="table-dark text-center sticky-top bg-dark" style={{ zIndex: 10 }}>
              <tr>
                {currentColumns.map((key) => (
                  <th key={key} onClick={() => handleSort(key)} style={{ cursor: "pointer" }}>
                    {columnLabels[key] || key} {sortConfig.key === key && (sortConfig.direction === "asc" ? "▲" : "▼")}
                  </th>
                ))}
                <th>{t("delete")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, rowIndex) => {
                const fullName = `${row.driver_firstName} ${row.driver_lastName}`;
                const isInvalid = invalidUsers.includes(fullName);

                return (
                  <tr key={rowIndex}>
                    {currentColumns.map((key) => {
                      const value = row[key] ?? "";
                      const isNameField = key === "driver_firstName" || key === "driver_lastName";

                      return (
                        <td
                          key={key}
                          onDoubleClick={() => setEditingCell({ row: rowIndex, key })}
                          style={{ cursor: "pointer", position: "relative" }}
                        >
                          {editingCell.row === rowIndex && editingCell.key === key ? (
                            <input
                              autoFocus
                              type="text"
                              className="form-control"
                              value={value}
                              onChange={(e) => handleInputChange(rowIndex, key, e.target.value)}
                              onBlur={() => setEditingCell({ row: null, key: null })}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") setEditingCell({ row: null, key: null });
                              }}
                            />
                          ) : (
                            <>
                              {value}
                              {isNameField && isInvalid && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>{t("noUser")}</Tooltip>}
                                >
                                  <span className="text-danger ms-1">⚠️</span>
                                </OverlayTrigger>
                              )}
                            </>
                          )}
                        </td>
                      );
                    })}
                    <td className="text-center">
                      <Button variant="danger" size="sm" onClick={() => deleteRecord(row.id)}>🗑️</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      <div className="d-flex justify-content-end">
        <Button variant="success" onClick={saveChanges} disabled={loading || tableData.length === 0}>
          💾 {t("save")}
        </Button>
      </div>
    </Container>
  );
};

export default EditableIncoming;